import type {
  AdminAccountListItem,
  AdminAccountListResponse,
  AdminUserRole,
  CreateAdminAccountRequest,
  ResetAdminPasswordRequest,
  UpdateAdminAccountRequest,
} from '@shared/types';
import { api } from './api';

/**
 * Admin account management API client.
 *
 * Scope: exclusively wraps `/api/admin/admin-users/*` — the superadmin-only
 * CRUD on the `AdminUser` collection. It deliberately has NO shared types or
 * endpoints with `services/users.ts`, which targets the PLAYER collection.
 * An admin account must never be looked up via the users client and a player
 * must never appear in any response surfaced by this module.
 */

export interface ListAdminAccountsParams {
  q?: string;
  page?: number;
  limit?: number;
}

function toQuery(params: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

export async function listAdminAccounts(
  params: ListAdminAccountsParams = {},
): Promise<AdminAccountListResponse> {
  const qs = toQuery({
    q: params.q,
    page: params.page ?? 1,
    limit: params.limit ?? 20,
  });
  return api<AdminAccountListResponse>(`/admin/admin-users${qs}`);
}

export async function createAdminAccount(
  body: CreateAdminAccountRequest,
): Promise<AdminAccountListItem> {
  return api<AdminAccountListItem>('/admin/admin-users', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateAdminAccount(
  id: string,
  body: UpdateAdminAccountRequest,
): Promise<AdminAccountListItem> {
  return api<AdminAccountListItem>(
    `/admin/admin-users/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

export async function resetAdminAccountPassword(
  id: string,
  body: ResetAdminPasswordRequest,
): Promise<AdminAccountListItem> {
  return api<AdminAccountListItem>(
    `/admin/admin-users/${encodeURIComponent(id)}/reset-password`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export async function resetAdminAccountMfa(
  id: string,
): Promise<AdminAccountListItem> {
  return api<AdminAccountListItem>(
    `/admin/admin-users/${encodeURIComponent(id)}/reset-mfa`,
    { method: 'POST' },
  );
}

export async function deleteAdminAccount(
  id: string,
): Promise<AdminAccountListItem> {
  return api<AdminAccountListItem>(
    `/admin/admin-users/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
}

/**
 * Export the role list so the UI can render the role-select dropdown. Order
 * is least → most privileged for display, with `content_manager` (the lateral
 * content-only role) grouped next to `viewer`. Mirrors `ADMIN_USER_ROLES`.
 */
export const ADMIN_ACCOUNT_ROLES: AdminUserRole[] = [
  'viewer',
  'content_manager',
  'admin',
  'superadmin',
];

/** Friendly labels for the role dropdown — the raw slugs are terse/ambiguous. */
export const ADMIN_ROLE_LABELS: Record<AdminUserRole, string> = {
  viewer: 'Viewer (read-only)',
  content_manager: 'Content Manager (game content only)',
  admin: 'Admin',
  superadmin: 'Superadmin',
};
